import { useMemo, useState } from 'react';
import { effectsByCategory } from '../gpu/registry';
import type { EffectCategory, EffectId } from '../gpu/types';

interface Props {
  current: EffectId;
  faceAvailable: boolean;
  onSelect(id: EffectId): void;
  /**
   * Called when the drawer opens. Lets the booth page kick off MediaPipe
   * loading so the user doesn't wait when they tap a face-tracked effect.
   */
  onOpen?(): void;
  /** If set, only effects with these IDs are shown. */
  allowedEffects?: readonly EffectId[];
}

const CATEGORY_LABELS: Record<EffectCategory, string> = {
  filter: 'Filters',
  distortion: 'Distortions',
  overlay: 'Overlays',
};

export function EffectDrawer({
  current,
  faceAvailable,
  onSelect,
  onOpen,
  allowedEffects,
}: Props) {
  const [open, setOpen] = useState(false);
  const grouped = useMemo(() => {
    const all = effectsByCategory();
    if (!allowedEffects) return all;
    const set = new Set(allowedEffects);
    return {
      filter: all.filter.filter((e) => set.has(e.id)),
      distortion: all.distortion.filter((e) => set.has(e.id)),
      overlay: all.overlay.filter((e) => set.has(e.id)),
    };
  }, [allowedEffects]);
  const allCategories: EffectCategory[] = ['filter', 'distortion', 'overlay'];

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setOpen((v) => {
            const next = !v;
            if (next && onOpen) onOpen();
            return next;
          });
        }}
        aria-expanded={open}
        aria-label="Open effects drawer"
        style={{
          minHeight: 'var(--tap)',
          minWidth: 'var(--tap)',
          padding: '10px 18px',
          background: 'rgba(0,0,0,0.6)',
          color: 'var(--text)',
          border: '1px solid rgba(255,255,255,0.18)',
          borderRadius: 999,
          fontWeight: 600,
          fontSize: 14,
        }}
      >
        Effects: {labelFor(current)}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Effect picker"
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            top: 'auto',
            maxHeight: '70vh',
            overflowY: 'auto',
            background: 'rgba(11,11,15,0.96)',
            backdropFilter: 'blur(20px)',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '20px 20px 0 0',
            padding: '18px 16px calc(20px + env(safe-area-inset-bottom, 0px))',
            zIndex: 20,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <strong>Effects</strong>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-dim)',
                fontSize: 22,
              }}
            >
              ×
            </button>
          </div>
          {allCategories.map((cat) => (
            <section key={cat} style={{ marginBottom: 14 }}>
              <h3
                style={{
                  margin: '4px 0 8px',
                  fontSize: 12,
                  textTransform: 'uppercase',
                  letterSpacing: 1.4,
                  color: 'var(--text-dim)',
                }}
              >
                {CATEGORY_LABELS[cat]}
                {/* Only flag the category as gated when EVERY effect in it
                    actually requires face tracking that isn't available. */}
                {!faceAvailable &&
                  grouped[cat].length > 0 &&
                  grouped[cat].every((e) => e.requiresFace) && (
                    <span style={{ marginLeft: 8, color: 'var(--danger)' }}>
                      (face tracking unavailable)
                    </span>
                  )}
              </h3>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: 8,
                }}
              >
                {grouped[cat].map((eff) => {
                  const disabled = eff.requiresFace && !faceAvailable;
                  const active = eff.id === current;
                  return (
                    <button
                      key={eff.id}
                      type="button"
                      onClick={() => {
                        if (disabled) return;
                        onSelect(eff.id);
                        setOpen(false);
                      }}
                      disabled={disabled}
                      style={{
                        padding: 10,
                        borderRadius: 12,
                        border: active
                          ? '2px solid var(--accent)'
                          : '1px solid rgba(255,255,255,0.1)',
                        background: active
                          ? 'rgba(255,95,162,0.16)'
                          : 'rgba(255,255,255,0.04)',
                        color: 'var(--text)',
                        fontWeight: 600,
                        fontSize: 13,
                        minHeight: 56,
                        textAlign: 'left',
                        opacity: disabled ? 0.4 : 1,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {eff.label}
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </>
  );
}

function labelFor(id: EffectId): string {
  const all = effectsByCategory();
  for (const cat of ['filter', 'distortion', 'overlay'] as const) {
    const e = all[cat].find((x) => x.id === id);
    if (e) return e.label;
  }
  return id;
}
